/**
 * I passi del tutorial guidato dell'elenco Fornitori.
 *
 * Due preparativi: il menu del piu' in alto (c'e' solo con Fattureincloud attivo,
 * altrimenti il piu' porta dritto alla pagina) e il menu della riga. Ogni passo
 * prepara quello che gli serve e `stopSuppliersListTour` rimette tutto com'era.
 *
 * I bersagli della pagina sono scoped; le voci dei menu no, perche' Vuetify
 * stacca il contenuto dei menu e lo appende a `[data-app]`, fuori dalla pagina.
 */
const at = name => `[data-tour-scope="suppliers-list"] [data-tour="${name}"]`;

const ADD_SUPPLIER = '[data-tour="add-supplier"]';
const ADD_FIC = '[data-tour="add-fic"]';
const ROW_EDIT = '[data-tour="row-edit"]';

function isVisible(selector) {
	const element = document.querySelector(selector);

	if (!element || element.getClientRects().length === 0) {
		return false;
	}

	/*
	 * Un menu che si sta chiudendo resta a schermo per tutta l'animazione, ma
	 * Vuetify gli toglie subito `menuable__content__active`. Senza questo
	 * controllo un menu in chiusura sembrerebbe ancora aperto e il click di
	 * pulizia, invece di chiuderlo, lo riaprirebbe.
	 */
	const content = element.closest('.v-menu__content');

	return !content || content.classList.contains('menuable__content__active');
}

/**
 * Lascia scattare la chiusura che Vuetify ha gia' in coda.
 *
 * I menu si chiudono al primo click fuori, e il click su "Avanti" e' fuori: la
 * direttiva l'ha registrato in cattura, prima ancora che il pulsante facesse il
 * suo lavoro. Riaprendo subito, il menu si richiuderebbe un istante dopo.
 */
function afterPendingClose() {
	return new Promise(resolve => setTimeout(resolve, 0));
}

/**
 * Aspetta che il bersaglio compaia o sparisca.
 *
 * L'attesa sta qui e non nel `timeout` del passo: cosi' si aspetta solo quando
 * c'e' davvero qualcosa da aprire, e a elenco vuoto il passo si aggancia subito
 * in basso invece di far aspettare a vuoto.
 */
async function waitFor(probe, wanted, timeout = 1500) {
	const deadline = performance.now() + timeout;

	while (performance.now() < deadline) {
		if (isVisible(probe) === wanted) {
			return;
		}

		await new Promise(resolve => setTimeout(resolve, 50));
	}
}

function click(selector) {
	const element = document.querySelector(selector);

	if (element) {
		element.click();
	}
}

async function openMenu(activator, probe) {
	await afterPendingClose();

	if (isVisible(probe)) {
		return;
	}

	const element = document.querySelector(activator);

	if (!element) {
		return;
	}

	element.click();
	await waitFor(probe, true);
}

async function closeMenus() {
	await afterPendingClose();

	// L'attivatore fa da interruttore: il secondo click richiude. Senza Fattureincloud
	// il piu' e' un bottone diretto (niente menu, niente `add`): non c'e' niente da chiudere.
	if (document.querySelector(at('add')) && isVisible(ADD_SUPPLIER)) {
		click(at('add'));
		await waitFor(ADD_SUPPLIER, false);
	}

	if (isVisible(ROW_EDIT)) {
		click(at('row-menu'));
		await waitFor(ROW_EDIT, false);
	}
}

const openAddMenu = () => openMenu(at('add'), ADD_SUPPLIER);
const openRowMenu = () => openMenu(at('row-menu'), ROW_EDIT);


/** Rimette la pagina com'era: menu chiusi. */
export async function stopSuppliersListTour() {
	await closeMenus();
}

export function createSuppliersListTourSteps() {
	return [
		{
			body: "Questa e' l'anagrafica di chi ti vende la merce, e non serve solo a tenere un elenco di nomi: il fornitore e' quello che scegli quando registri un acquisto, quello a cui il sistema aggancia i prodotti che entrano in magazzino, e quello a cui parte l'ordine fornitore via mail. Tenerla in ordine fa risparmiare tempo a valle.",
			key: 'intro',
			placement: 'bottom',
			target: at('tutorial'),
			title: 'A cosa serve questa pagina',
		},
		{
			body: "Cerca nel nome dell'azienda, e solo li': email e telefono non vengono guardati. Funziona da quattro caratteri in su - sotto, la ricerca parte ma viene ignorata e l'elenco resta intero. Si conferma con Invio o con la lente.",
			before: closeMenus,
			key: 'search',
			placement: 'bottom',
			target: at('search'),
			title: 'Cercare un fornitore',
		},
		{
			body: "Le colonne sono quattro: la ragione sociale, l'email, il telefono e References. Il collegamento fra fornitore e prodotti non si fa a mano da qui - nasce da solo quando registri un acquisto o sincronizzi un documento da Fattureincloud - e References ti dice quanti prodotti sono collegati a quel fornitore. Ordinando per References vedi subito da chi compri di piu'.",
			key: 'table',
			placement: 'top',
			target: at('table'),
			timeout: 250,
			title: 'Come si legge la tabella',
		},
		{
			body: "I tre pallini a inizio riga aprono due voci: Modifica apre la pagina del fornitore ed Elimina lo cancella, chiedendo conferma. Prima di eliminare, un pensiero: i prodotti che quel fornitore ti vende restano senza il riferimento a chi te li vende, e gli acquisti gia' registrati non sapranno piu' da chi venivano.",
			before: openRowMenu,
			key: 'row-edit',
			placement: 'right',
			target: ROW_EDIT,
			timeout: 250,
			title: 'Il menu della riga',
		},
		{
			body: "Il piu' (o la sua voce Aggiungi fornitore, quando il menu c'e' perche' e' attivo Fattureincloud) apre la pagina del nuovo fornitore: poche caselle, ma una conta piu' delle altre ed e' spiegata nel tutorial della scheda stessa.",
			before: openAddMenu,
			key: 'add-supplier',
			placement: 'left',
			target: ADD_SUPPLIER,
			title: 'Aggiungere un fornitore',
		},
		{
			body: "Importa da Fattureincloud riprende le anagrafiche gia' presenti nel gestionale invece di riscriverle: e' la strada consigliata se fatturi da li', perche' i nomi arrivano identici a come sono scritti sui documenti - e su quella identita' si regge l'aggancio automatico degli acquisti. La voce compare solo se il collegamento a Fattureincloud e' attivo.",
			before: openAddMenu,
			key: 'add-fic',
			placement: 'left',
			target: ADD_FIC,
			timeout: 250,
			title: 'Importare da Fattureincloud',
		},
	];
}
